/**
 * What happens to money collected through our own provider account.
 *
 * When an agency uses **its own** gateway keys, none of this applies: the money never touches us
 * and the agency reconciles with its provider. When it uses **ours**, the payment lands in our
 * account and we owe the agency the rest of it — so three questions have to be answered exactly,
 * in integers, every time:
 *
 * 1. what the provider kept,
 * 2. what we keep,
 * 3. what the agency is owed.
 *
 * **The three must add up to the gross, always.** A rounding rule that loses a kobo per
 * transaction loses real money across a month and, worse, produces a settlement statement that
 * does not reconcile — which is the one thing an agency will notice immediately and never trust
 * again. Every function here works in minor units and the sum is asserted by the tests.
 *
 * **Fees are deducted, never added.** The tenant is charged the invoice amount and nothing more.
 * Passing the gateway's fee on to the payer is a decision the product has not made, and doing it
 * silently would mean the amount on the invoice and the amount on the card statement differ.
 */

import type { BasisPoints, Minor } from "./money";

/** Who is paying, which decides whether we take anything. */
export type ChargePurpose = "rent" | "subscription";

/**
 * What a provider charges us, as we currently understand it.
 *
 * Deliberately a table in the codebase rather than a per-agency setting: these are *our* commercial
 * terms with the provider, the same for every agency, and an agency editing them would only ever
 * make the settlement statement wrong. The numbers are the published rates and are the first thing
 * to check against a real statement once the accounts are live — see the caveat in the handoff.
 */
export interface ProviderFee {
  /** Percentage of the gross, in basis points. */
  bp: BasisPoints;
  /** Added to the percentage, in minor units. Zero for most. */
  flat: Minor;
  /** The most the provider will take from one transaction. Zero means uncapped. */
  cap: Minor;
  /** Below this, the flat part is waived. Zero means it never is. */
  flatWaivedBelow: Minor;
}

export const PROVIDER_FEES: Record<string, Record<string, ProviderFee>> = {
  paystack: {
    NGN: { bp: 150, flat: 10000, cap: 200000, flatWaivedBelow: 250000 },
    GHS: { bp: 195, flat: 0, cap: 0, flatWaivedBelow: 0 },
    ZAR: { bp: 290, flat: 100, cap: 0, flatWaivedBelow: 0 },
    KES: { bp: 290, flat: 0, cap: 0, flatWaivedBelow: 0 },
    USD: { bp: 390, flat: 30, cap: 0, flatWaivedBelow: 0 },
  },
  flutterwave: {
    NGN: { bp: 140, flat: 0, cap: 200000, flatWaivedBelow: 0 },
    GHS: { bp: 195, flat: 0, cap: 0, flatWaivedBelow: 0 },
    KES: { bp: 340, flat: 0, cap: 0, flatWaivedBelow: 0 },
    UGX: { bp: 340, flat: 0, cap: 0, flatWaivedBelow: 0 },
    TZS: { bp: 340, flat: 0, cap: 0, flatWaivedBelow: 0 },
    ZAR: { bp: 290, flat: 0, cap: 0, flatWaivedBelow: 0 },
    USD: { bp: 380, flat: 0, cap: 0, flatWaivedBelow: 0 },
  },
};

/** Our terms with the provider for one currency, or a conservative default if we have none. */
export function providerFeeFor(provider: string, currency: string): ProviderFee {
  const byCurrency = PROVIDER_FEES[provider];
  const known = byCurrency?.[currency.toUpperCase()];
  if (known) return known;
  // Unknown pairing: assume the worst published rate we hold rather than assuming free, so a
  // settlement is never promised larger than it turns out to be.
  return { bp: 390, flat: 0, cap: 0, flatWaivedBelow: 0 };
}

/**
 * What the platform keeps on a rent payment collected through our account.
 *
 * **Zero on subscriptions**, because an agency paying us for the product is not a transaction we
 * take a cut of — the whole amount is ours already, less what the provider took.
 *
 * The rate is a commercial decision and is a placeholder until it is set: see the outstanding
 * decision in the handoff. It lives here so there is exactly one place to change it.
 */
export interface PlatformFee {
  bp: BasisPoints;
  flat: Minor;
  cap: Minor;
}

export const DEFAULT_PLATFORM_FEE: PlatformFee = { bp: 50, flat: 0, cap: 250000 };

/** Round half away from zero, so the same amount always splits the same way. */
function round(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

function applyFee(gross: Minor, bp: BasisPoints, flat: Minor, cap: Minor, flatWaivedBelow = 0): Minor {
  const percentage = round((gross * bp) / 10_000);
  const withFlat = percentage + (flatWaivedBelow > 0 && gross < flatWaivedBelow ? 0 : flat);
  const capped = cap > 0 ? Math.min(withFlat, cap) : withFlat;
  // A fee larger than the payment itself is arithmetic, not a fee.
  return Math.max(0, Math.min(capped, gross));
}

export interface ChargeSplit {
  gross: Minor;
  /** What the provider keeps. */
  providerFee: Minor;
  /** What we keep. Zero on a subscription — the money is already ours. */
  platformFee: Minor;
  /** What the agency is owed, and what a settlement pays out. */
  net: Minor;
}

/**
 * Split a charge three ways.
 *
 * The order matters: the provider takes its cut of the gross first, because that is what actually
 * happens in their account, and our fee comes out of what is left. Taking ours off the gross too
 * would make the two fees compound in a way no statement would explain.
 */
export function splitCharge(
  gross: Minor,
  opts: { provider: string; currency: string; purpose: ChargePurpose; platformFee?: PlatformFee },
): ChargeSplit {
  const amount = Math.max(0, Math.trunc(gross));
  const provider = providerFeeFor(opts.provider, opts.currency);
  const providerFee = applyFee(amount, provider.bp, provider.flat, provider.cap, provider.flatWaivedBelow);

  const remaining = amount - providerFee;
  const terms = opts.platformFee ?? DEFAULT_PLATFORM_FEE;
  const platformFee = opts.purpose === "subscription" ? 0 : applyFee(remaining, terms.bp, terms.flat, terms.cap);

  return { gross: amount, providerFee, platformFee, net: remaining - platformFee };
}

/**
 * What a settlement run pays, given the charges it covers.
 *
 * A refunded charge settles nothing: the money went back to the payer, and paying it out as well
 * would have us funding the refund twice. A partially refunded one settles the remainder, with the
 * fees left where they fell — providers do not return their fee on a refund, and pretending they
 * do would make the run short by exactly that amount.
 */
export interface SettleableCharge {
  gross: Minor;
  providerFee: Minor;
  platformFee: Minor;
  refunded: Minor;
}

export interface SettlementTotals {
  gross: Minor;
  providerFees: Minor;
  platformFees: Minor;
  refunded: Minor;
  /** What is actually transferred to the agency. Never negative. */
  net: Minor;
  count: number;
}

export function settlementTotals(charges: readonly SettleableCharge[]): SettlementTotals {
  const totals = charges.reduce<SettlementTotals>(
    (running, charge) => {
      const refunded = Math.min(Math.max(0, charge.refunded), charge.gross);
      return {
        gross: running.gross + charge.gross,
        providerFees: running.providerFees + charge.providerFee,
        platformFees: running.platformFees + charge.platformFee,
        refunded: running.refunded + refunded,
        net: running.net + (charge.gross - charge.providerFee - charge.platformFee - refunded),
        count: running.count + 1,
      };
    },
    { gross: 0, providerFees: 0, platformFees: 0, refunded: 0, net: 0, count: 0 },
  );

  // A run that owes nothing is a run of zero, not a debt. A negative total means refunds outran
  // collections in the period, and that is carried by the next run rather than invoiced back.
  return { ...totals, net: Math.max(0, totals.net) };
}

/** Whatever a negative run would have been, carried forward rather than lost. */
export function carriedForward(charges: readonly SettleableCharge[]): Minor {
  const raw = charges.reduce(
    (running, charge) =>
      running + (charge.gross - charge.providerFee - charge.platformFee - Math.min(Math.max(0, charge.refunded), charge.gross)),
    0,
  );
  return raw < 0 ? -raw : 0;
}
